import type {BoardData} from '@github-ui/board-core/types'
import {SafeHTML, Type} from '@github-ui/react-query-persister/typebox'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'

/**
 * Contract scaffolding for M1: what the Rails serialiser produces and the browser maps into
 * `BoardData`. Pinned by `__tests__/fixtures/board-payload.json`, whose sibling copy in `github/github`
 * is kept in sync by hand — a shape change on either side must be mirrored in the other's
 * fixture.
 * This should eventually be replaced by Protos https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/e2e-types/.
 */

// --- Enums ---------------------------------------------------------------

const IssueStateSchema = Type.Union([Type.Literal('open'), Type.Literal('closed')])
export type IssueState = Type.Static<typeof IssueStateSchema>

const IssueStateReasonSchema = Type.Union([
  Type.Literal('not_planned'),
  Type.Literal('completed'),
  Type.Literal('reopened'),
  Type.Literal('duplicate'),
])
export type IssueStateReason = Type.Static<typeof IssueStateReasonSchema>

const PullRequestStateSchema = Type.Union([
  Type.Literal('draft'),
  Type.Literal('open'),
  Type.Literal('closed'),
  Type.Literal('merged'),
  Type.Literal('queued'),
  Type.Literal('archived'),
])
export type PullRequestState = Type.Static<typeof PullRequestStateSchema>

export type RepositoryStatus = 'archived' | 'forked' | 'public' | 'private'

// --- Leaf entities -------------------------------------------------------

/** Author and assignee share a shape: both carry the agent/copilot flags. `isCopilot` implies `isAgent`. */
const ActorSchema = Type.Object({
  login: Type.String(),
  name: Type.Union([Type.String(), Type.Null()]),
  avatarUrl: Type.String(),
  isAgent: Type.Boolean(),
  isCopilot: Type.Boolean(),
})
export type BoardPayloadAuthor = Type.Static<typeof ActorSchema>
export type BoardPayloadAssignee = Type.Static<typeof ActorSchema>

const LabelSchema = Type.Object({
  id: Type.Number(),
  name: Type.String(),
  nameHtml: Type.String(),
  color: Type.String(),
  description: Type.Union([Type.String(), Type.Null()]),
})
export type BoardPayloadLabel = Type.Static<typeof LabelSchema>

const MilestoneSchema = Type.Object({
  title: Type.String(),
  url: Type.String(),
  state: Type.Union([Type.Literal('open'), Type.Literal('closed')]),
  dueOn: Type.Union([Type.String(), Type.Null()]),
})
export type BoardPayloadMilestone = Type.Static<typeof MilestoneSchema>

const RepositorySchema = Type.Object({
  nameWithOwner: Type.String(),
  /**
   * Kept as a string so newer server statuses use the generic repository icon until this
   * client learns their specific presentation.
   */
  repositoryStatus: Type.String(),
})
export type BoardPayloadRepository = Type.Static<typeof RepositorySchema>

const IssueTypeSchema = Type.Object({name: Type.String(), color: Type.String()})
export type BoardPayloadIssueType = Type.Static<typeof IssueTypeSchema>

const ParentIssueSchema = Type.Object({
  // Server-rendered, so a title's emoji and inline code draw as they do on the issue.
  titleHtml: SafeHTML(),
  number: Type.Number(),
  state: IssueStateSchema,
  stateReason: Type.Union([IssueStateReasonSchema, Type.Null()]),
  url: Type.String(),
  updatedAt: Type.String(),
})
export type BoardPayloadParentIssue = Type.Static<typeof ParentIssueSchema>

/** Shaped as the issues list's `IssueLinkedPullRequestSummary`, so both render with `LinkedPullRequests`. */
const LinkedPullRequestSchema = Type.Object({
  id: Type.Number(),
  title: Type.String(),
  number: Type.Number(),
  permalink: Type.String(),
  repoNameWithOwner: Type.String(),
  /** `OPEN`, `CLOSED` or `MERGED`; a draft is `OPEN` with `isDraft`. */
  state: Type.String(),
  isDraft: Type.Boolean(),
})
export type BoardPayloadLinkedPullRequest = Type.Static<typeof LinkedPullRequestSchema>

// --- Field slots ---------------------------------------------------------

/**
 * Field slots keyed by the registered identifier from `identifiers-findings.md`. This is
 * the object the card joins against the field descriptor: `descriptor.fields[f].identifier
 * → item.fields[<same identifier>].value`.
 *
 * **Keys are the identifier verbatim** — no camelCase, snake_case, plural, hyphen, or
 * alias transform. The identifier is simultaneously the URL token, sort token, descriptor
 * key, and payload key; they must be one string so `descriptor.fields[f]` and
 * `item.fields[f]` line up (D26 / github/views#55).
 *
 * **Every slot is `Type.Optional` for M1.** This contract is hand-synced with Rails.
 * Optional starts loose and can be tightened per-field once a captured fixture proves
 * the field is always sent. Absent key = "producer didn't deliver"; present with
 * `{value: []}` or `{value: null}` = "delivered, no value" — a card with no labels
 * still ships `label: {value: []}`.
 */
const LabelFieldSchema = Type.Object({value: Type.Array(LabelSchema)})
export type LabelField = Type.Static<typeof LabelFieldSchema>

const MilestoneFieldSchema = Type.Object({value: Type.Union([MilestoneSchema, Type.Null()])})
export type MilestoneField = Type.Static<typeof MilestoneFieldSchema>

const IssueTypeFieldSchema = Type.Object({value: Type.Union([IssueTypeSchema, Type.Null()])})
export type IssueTypeField = Type.Static<typeof IssueTypeFieldSchema>

const ParentIssueFieldSchema = Type.Object({value: Type.Union([ParentIssueSchema, Type.Null()])})
export type ParentIssueField = Type.Static<typeof ParentIssueFieldSchema>

/**
 * The pull requests that will close an issue, in number order and at most ten, with `totalCount`
 * counting all of them. Deferred: a card never carries it, and it arrives from
 * `deferred-payload.ts` once the card is drawn.
 */
export const LinkedPullRequestsFieldSchema = Type.Object({
  value: Type.Array(LinkedPullRequestSchema),
  totalCount: Type.Number(),
})
export type LinkedPullRequestsField = Type.Static<typeof LinkedPullRequestsFieldSchema>

/** Where a pull request sits in its stack. `position` is one-based, where 1 is the bottom. */
const StackedPullRequestsSchema = Type.Object({position: Type.Number(), size: Type.Number()})
export type BoardPayloadStackedPullRequests = Type.Static<typeof StackedPullRequestsSchema>

/**
 * Null for a pull request in no stack, or in a repository without stacks. Deferred, like
 * `LinkedPullRequestsFieldSchema`.
 */
export const StackedPullRequestsFieldSchema = Type.Object({value: Type.Union([StackedPullRequestsSchema, Type.Null()])})
export type StackedPullRequestsField = Type.Static<typeof StackedPullRequestsFieldSchema>

// Null when the issue has no sub-issues.
const SubIssuesProgressSchema = Type.Object({
  total: Type.Number(),
  completed: Type.Number(),
  percentCompleted: Type.Number(),
})
export type BoardPayloadSubIssuesProgress = Type.Static<typeof SubIssuesProgressSchema>

const SubIssuesProgressFieldSchema = Type.Object({value: Type.Union([SubIssuesProgressSchema, Type.Null()])})
export type SubIssuesProgressField = Type.Static<typeof SubIssuesProgressFieldSchema>

const CreatedFieldSchema = Type.Object({value: Type.String()})
export type CreatedField = Type.Static<typeof CreatedFieldSchema>

const UpdatedFieldSchema = Type.Object({value: Type.String()})
export type UpdatedField = Type.Static<typeof UpdatedFieldSchema>

// Null until the item is closed or merged.
const ClosedFieldSchema = Type.Object({value: Type.Union([Type.String(), Type.Null()])})
export type ClosedField = Type.Static<typeof ClosedFieldSchema>

const AuthorFieldSchema = Type.Object({value: ActorSchema})
export type AuthorField = Type.Static<typeof AuthorFieldSchema>

// Multi-assignee: value is an array, empty when the item has no assignees.
const AssigneeFieldSchema = Type.Object({value: Type.Array(ActorSchema)})
export type AssigneeField = Type.Static<typeof AssigneeFieldSchema>

const RepositoryFieldSchema = Type.Object({value: Type.Union([RepositorySchema, Type.Null()])})
export type RepositoryField = Type.Static<typeof RepositoryFieldSchema>

/**
 * A custom select field's chosen option. The colour is a lowercase role name (`blue`), kept a
 * plain string so a colour this client does not know yet still validates.
 */
const SelectOptionSchema = Type.Object({id: Type.Number(), name: Type.String(), color: Type.String()})
export type BoardPayloadSelectOption = Type.Static<typeof SelectOptionSchema>
const selectOptionValidator = Compile(SelectOptionSchema)

export function isBoardPayloadSelectOption(value: unknown): value is BoardPayloadSelectOption {
  return selectOptionValidator.Check(value)
}

/**
 * A custom issue field's slot, keyed `field.<id>`. Which value it holds depends on the field's
 * data type, which only the field definition says: a string for text and date, a number, one
 * option for a single-select, a list of options for a multi-select, or `null` when unset.
 *
 * The value is left unchecked here so one malformed field cannot fail a whole payload or page.
 * Readers check it against the field's data type, e.g. with `isBoardPayloadSelectOption`.
 */
const CustomFieldSchema = Type.Object({value: Type.Unknown()})
export type CustomField = Type.Static<typeof CustomFieldSchema>
export type CustomFieldIdentifier = `field.${number}`

const IssueFieldsSchema = Type.Intersect([
  Type.Object({
    label: Type.Optional(LabelFieldSchema),
    milestone: Type.Optional(MilestoneFieldSchema),
    created: Type.Optional(CreatedFieldSchema),
    updated: Type.Optional(UpdatedFieldSchema),
    closed: Type.Optional(ClosedFieldSchema),
    author: Type.Optional(AuthorFieldSchema),
    assignee: Type.Optional(AssigneeFieldSchema),
    repository: Type.Optional(RepositoryFieldSchema),
    // Issue-only field.
    issue_type: Type.Optional(IssueTypeFieldSchema),
    // Also issue-only. Hyphen is verbatim from the registry — forces bracket access at reads.
    'parent-issue': Type.Optional(ParentIssueFieldSchema),
    'linked-pull-requests': Type.Optional(LinkedPullRequestsFieldSchema),
    'sub-issues-progress': Type.Optional(SubIssuesProgressFieldSchema),
  }),
  // Custom fields are issue-only too.
  Type.Record(Type.TemplateLiteral('field.${number}'), CustomFieldSchema),
])
export type IssueFields = Type.Static<typeof IssueFieldsSchema>

// Unknown fields are allowed so a client deployed before the server adds a field keeps working.
const PullRequestFieldsSchema = Type.Object({
  label: Type.Optional(LabelFieldSchema),
  milestone: Type.Optional(MilestoneFieldSchema),
  created: Type.Optional(CreatedFieldSchema),
  updated: Type.Optional(UpdatedFieldSchema),
  closed: Type.Optional(ClosedFieldSchema),
  author: Type.Optional(AuthorFieldSchema),
  assignee: Type.Optional(AssigneeFieldSchema),
  repository: Type.Optional(RepositoryFieldSchema),
  // Pull-request-only field.
  'stacked-pull-requests': Type.Optional(StackedPullRequestsFieldSchema),
})

export type PullRequestFields = Type.Static<typeof PullRequestFieldsSchema>

// --- Item shape ----------------------------------------------------------

const ItemBaseSchema = Type.Object({
  /** Database id: unstable across hosts, kept for the client's own bookkeeping. */
  id: Type.Number(),
  /** Globally-unique identity; safe as a render or cache key across a mixed cell. */
  globalId: Type.String(),
  number: Type.Number(),
  title: Type.String(),
  titleHtml: Type.String(),
  permalink: Type.String(),
  repoNameWithOwner: Type.String(),
  /**
   * Whether this viewer may change every field this board arranges by, on this item. Read
   * only as the fallback for the two per-axis flags, from a server that does not send them.
   *
   * Absent reads as `false`: a drag the server will refuse is worse than no drag at all.
   */
  canMove: Type.Optional(Type.Boolean()),
  /**
   * Whether this viewer may change the field the columns are grouped by, on this item.
   * Computed by the field layer, never rebuilt in the browser from anything else.
   *
   * Optional so this client works against a server that predates it.
   */
  canMoveBetweenColumns: Type.Optional(Type.Boolean()),
  /** The same for the field the rows are grouped by. */
  canMoveBetweenRows: Type.Optional(Type.Boolean()),
})

const IssueItemSchema = Type.Intersect([
  ItemBaseSchema,
  Type.Object({
    type: Type.Literal('issue'),
    state: IssueStateSchema,
    stateReason: Type.Union([IssueStateReasonSchema, Type.Null()]),
    fields: IssueFieldsSchema,
    /**
     * Whether this issue belongs to an account rather than a repository, in which case
     * `permalink` is its `/orgs/<org>/issues/<number>` or `/users/<user>/issues/<number>` path.
     * Optional so this client works against a server that does not send it yet.
     */
    isAccountIssue: Type.Optional(Type.Boolean()),
  }),
])
export type BoardPayloadIssueItem = Type.Static<typeof IssueItemSchema>

const PullRequestItemSchema = Type.Intersect([
  ItemBaseSchema,
  Type.Object({
    type: Type.Literal('pull_request'),
    state: PullRequestStateSchema,
    fields: PullRequestFieldsSchema,
  }),
])
export type BoardPayloadPullRequestItem = Type.Static<typeof PullRequestItemSchema>

/** Issues and pull requests share a cell, discriminated by `type`. */
const ItemSchema = Type.Union([IssueItemSchema, PullRequestItemSchema])
export type BoardPayloadItem = Type.Static<typeof ItemSchema>

// --- Axes, cells, top-level ---------------------------------------------

/**
 * The key the server reserves for the bucket holding items with no value for an axis
 * (`Issues::Board::Payload::NO_VALUE_KEY`). It arrives under an empty label because the
 * wording is the client's (github/views#137); `toBoardAxisValues` names it.
 */
export const NO_VALUE_KEY = '__NO_VALUE__'

/**
 * The role colours an axis bucket can be drawn in, verbatim from the server's
 * `IssueSearchField::OptionColor`. Named rather than a hex value so each one resolves to a
 * Primer role token and follows the reader's theme; `@github-ui/use-named-color` owns that
 * mapping and names the same eight in upper case.
 */
const AxisColorSchema = Type.Union([
  Type.Literal('gray'),
  Type.Literal('blue'),
  Type.Literal('green'),
  Type.Literal('yellow'),
  Type.Literal('orange'),
  Type.Literal('red'),
  Type.Literal('pink'),
  Type.Literal('purple'),
])
export type AxisColor = Type.Static<typeof AxisColorSchema>

/**
 * What a bucket is drawn in, for a field whose buckets carry a colour at all.
 *
 * Tagged rather than two optional members side by side, because the two forms are drawn
 * differently and only one follows the reader's theme: `named` is one of eight role names that
 * resolves to a Primer token, `hex` is the literal value an account stored for a label. Stating
 * which it is keeps a role name from being read as a literal, which would freeze it to whatever
 * the light theme resolves to today.
 */
const AxisBucketColorSchema = Type.Union([
  Type.Object({type: Type.Literal('named'), name: AxisColorSchema}),
  // Six lowercase hex digits with no leading `#`, which is how the server states a label's
  // colour and how a heading interpolates it into a style. Pinned here because an
  // unconstrained string reaches that style as invalid CSS rather than as a failed parse.
  Type.Object({type: Type.Literal('hex'), hex: Type.String({pattern: '^[0-9a-f]{6}$'})}),
])
export type BoardPayloadAxisColor = Type.Static<typeof AxisBucketColorSchema>

/**
 * Everything one axis bucket is drawn from: the text its heading reads, and whatever else the
 * field that produced the bucket gives the browser to draw it with.
 *
 * An object rather than members beside the key, because what a bucket needs varies by field: a
 * select option has one of eight role colours, a label has an arbitrary hex one and
 * server-rendered markup for its emoji, an assignee has an avatar.
 */
const AxisLabelSchema = Type.Object({
  text: Type.String(),
  /**
   * The heading as server-rendered markup, for a field whose names carry emoji. Absent where
   * there is nothing to draw, so a present value always means markup and `text` is what names
   * the bucket otherwise.
   *
   * `SafeHTMLString` because this is where the server's rendering enters the browser, and a
   * heading renders it as markup. Anything reaching a producer of this payload from anywhere
   * but the HTML pipeline belongs in `text`, which renders as text.
   */
  textHtml: Type.Optional(SafeHTML()),
  /** Drawn beside the text, for a field whose buckets carry a colour. */
  color: Type.Optional(AxisBucketColorSchema),
  /**
   * Drawn beside the text, for a field whose buckets are accounts. `text` is the login, so the
   * avatar adds nothing a screen reader needs.
   */
  avatarUrl: Type.Optional(Type.String()),
  /**
   * Drawn beside the text, for a field whose buckets are repositories. Kept as a string so
   * newer server statuses can fall back to a generic repository icon.
   */
  repositoryStatus: Type.Optional(Type.String()),
  /**
   * Drawn beside the text as the status icon, for a field whose buckets are issue statuses.
   * The icon follows the status's category key, such as `started`. Kept as strings so a status
   * without a category, or in one the browser does not know, falls back to the open or closed
   * icon.
   */
  issueStatus: Type.Optional(
    Type.Object({name: Type.String(), state: Type.String(), category: Type.Optional(Type.String())}),
  ),
})
export type BoardPayloadAxisLabel = Type.Static<typeof AxisLabelSchema>

const ColumnSchema = Type.Object({
  key: Type.String(),
  label: AxisLabelSchema,
  /** The server's count for the whole column. Never a sum of its cells. */
  totalCount: Type.Number(),
})
export type BoardPayloadColumn = Type.Static<typeof ColumnSchema>

const RowSchema = Type.Object({
  key: Type.String(),
  label: AxisLabelSchema,
  totalCount: Type.Number(),
})
export type BoardPayloadRow = Type.Static<typeof RowSchema>

const CellSchema = Type.Object({
  columnKey: Type.String(),
  rowKey: Type.String(),
  /** The server's count for this cell. Never `items.length`. */
  totalCount: Type.Number(),
  /**
   * Whether the cards were fetched for this cell. Counts cover the whole loaded grid while cards
   * stop at the region the board named, so `false` leaves `items`, `hasMoreItems` and
   * `itemsCursor` saying nothing about it: counted, never asked for (github/views#192).
   * Optional while older servers do not state it.
   */
  itemsLoaded: Type.Optional(Type.Boolean()),
  hasMoreItems: Type.Boolean(),
  /** Opaque cursor reserved for cell pagination, which the board does not consume yet. */
  itemsCursor: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  items: Type.Array(ItemSchema),
})
export type BoardPayloadCell = Type.Static<typeof CellSchema>

export const BoardInitialPayloadSchema = Type.Object({
  columns: Type.Array(ColumnSchema),
  rows: Type.Array(RowSchema),
  /**
   * Registry `identifier` of the field the columns are grouped by, e.g. `label`, or `null`
   * for a board with no grouping chosen yet (D29 / github/views#210). Explicitly stated
   * rather than absent so "not arranged yet" is a positive fact, matching `rowField` below.
   */
  columnField: Type.Union([Type.String(), Type.Null()]),
  /**
   * Same for rows, or `null` for a board with no swimlane (D29 / github/views#77).
   * Explicitly stated rather than absent so "no grouping" is a positive fact.
   */
  rowField: Type.Union([Type.String(), Type.Null()]),
  /**
   * The axis fields' own names. The field list is fetched separately and after the board is
   * drawn, so the identifiers above cannot be resolved to a name while an axis renders one.
   * Null exactly when the identifier beside it is.
   *
   * Optional so this client can be deployed before the server emits them, which is the order
   * the two are released in. Absent reads the same as null.
   */
  columnFieldLabel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  rowFieldLabel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  /** Only the populated pairs. An absent pair is an empty cell unless `cellsTruncated`. */
  cells: Type.Array(CellSchema),
  hasMoreColumns: Type.Boolean(),
  hasMoreRows: Type.Boolean(),
  columnsTruncated: Type.Boolean(),
  rowsTruncated: Type.Boolean(),
  /** Optional until the board distinguishes a truncated cell grid from an empty cell. */
  cellsTruncated: Type.Optional(Type.Boolean()),
  /**
   * Transcribed from the grouping field's own `countsOverlap` declaration — read once
   * off the field, never summed from the columns or recomputed here. A field placing
   * one item in several buckets says so once, on the field (github/views#55). `label`
   * and `review` are both overlapping registered fields; this flag copies whichever
   * produced the axis buckets (`columnField`, then `rowField`).
   */
  countsOverlap: Type.Boolean(),
  /** Opaque axis cursors carried outside the board-core data contract. */
  columnsCursor: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  rowsCursor: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  pageSize: Type.Optional(Type.Number()),
  /**
   * The repository-level writability fact for every axis at once. Read only as the fallback
   * for the two per-axis flags, from a server that does not send them.
   *
   * Optional and absent-reads-as-`false`, for the reason `canMove` is.
   */
  viewerCanMoveItems: Type.Optional(Type.Boolean()),
  /**
   * The repository-level writability fact for the column field, which gates whether the drag
   * affordance renders at all. Separate from a card's own flags and not interchangeable with
   * them: these decide whether any card may be picked up, those decide which.
   */
  viewerCanMoveBetweenColumns: Type.Optional(Type.Boolean()),
  /** The same for the row field. */
  viewerCanMoveBetweenRows: Type.Optional(Type.Boolean()),
  /**
   * What the server actually searched, after resolving the view's query and any edited one
   * previewed over it. A later page is drawn from this, since a cursor resumed under an edited
   * query, or one whose `sort:` differs, reads a different sequence than it was minted in.
   * Optional so this client can ship before the server states it.
   */
  query: Type.Optional(Type.String()),
})
export type BoardInitialPayload = Type.Static<typeof BoardInitialPayloadSchema>

export const boardInitialPayloadValidator = Compile(BoardInitialPayloadSchema)

/**
 * What a heading draws besides the count, keyed by bucket. Built from the payload's own axis
 * rather than from the cards in it, so a bucket nothing sits in is drawn the same as one that
 * is full, and a cell the board has not loaded costs nothing to read.
 *
 * One map per axis rather than one per kind of material: a bucket's colour, its markup and
 * whatever a field carries next all arrive together on `label`, and splitting them apart here
 * would put every field's buckets back into a row of optional side maps.
 */
export type BoardAxisLabels = ReadonlyMap<string, BoardPayloadAxisLabel>

export function axisLabels(axis: ReadonlyArray<{key: string; label: BoardPayloadAxisLabel}>): BoardAxisLabels {
  const labels = new Map<string, BoardPayloadAxisLabel>()
  for (const entry of axis) {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- `key` here is the axis entry's wire-contract key, not a keyboard event's
    labels.set(entry.key, entry.label)
  }
  return labels
}

/**
 * Runtime guard used to assert a parsed JSON value matches the contract. Importing JSON
 * widens string literals to `string`, so the `type` discriminant is only verifiable here.
 */
export function isBoardInitialPayload(value: unknown): value is BoardInitialPayload {
  return boardInitialPayloadValidator.Check(value)
}

type BoardPayloadValidationError = ReturnType<typeof boardInitialPayloadValidator.Errors>[number]

/**
 * Returns the most relevant validation error for a board payload, or `undefined` if it is valid.
 *
 * Each item is checked against both the issue schema and the pull request schema, and errors
 * from both are reported. This can be misleading; so this skips errors from any schema whose `type`
 * doesn't match the item's `type`. A pull request is reported against the pull request
 * schema and an issue against the issue schema.
 */
export function boardInitialPayloadError(value: unknown): BoardPayloadValidationError | undefined {
  const errors = boardInitialPayloadValidator.Errors(value)
  // Schemas whose `type` didn't match, e.g. `.../anyOf/0` for the issue schema on a pull request.
  const mismatchedSchemaPaths = errors
    .filter(error => error.keyword === 'const' && error.instancePath.endsWith('/type'))
    .map(error => /^.*\/anyOf\/\d+/.exec(error.schemaPath)?.[0])
    .filter(path => path !== undefined)
  const relevant = errors.filter(
    error => !mismatchedSchemaPaths.some(path => error.schemaPath === path || error.schemaPath.startsWith(`${path}/`)),
  )
  // Skip the generic "must match a schema in anyOf" in favor of the specific error behind it.
  return relevant.find(error => error.keyword !== 'anyOf') ?? relevant[0] ?? errors[0]
}

/**
 * What the no-value bucket is called on an axis whose field the payload did not name: a
 * response from a server that predates `columnFieldLabel`.
 */
const UNNAMED_NO_VALUE_LABEL = 'No value'

/**
 * What the bucket holding the items with no value for an axis is called, in the axis field's
 * own words: `No Priority` for a board arranged by `Priority`.
 *
 * The name comes from the payload rather than from the field list, which a saved view fetches in
 * the browser after the board has already been server-rendered (`useSavedViewFields`). Reading
 * it there would leave the server-rendered heading blank and rename it on hydration.
 */
function noValueLabel(fieldLabel: string | null | undefined): string {
  return fieldLabel == null ? UNNAMED_NO_VALUE_LABEL : `No ${fieldLabel}`
}

/**
 * Maps one axis into the board's contract, with the heading flattened to its text and the
 * reserved no-value entry named after the axis field.
 *
 * Named here rather than in a column header, because `label` is also what `useBoardCell`
 * and `useBoardRow` build their accessible names from: a heading fixed on its own would
 * leave every cell in the column announced under an empty name.
 */
export function toBoardAxisValues(
  axis: ReadonlyArray<{key: string; label: BoardPayloadAxisLabel; totalCount: number}>,
  fieldLabel: string | null | undefined,
): Array<{key: string; label: string; totalCount: number}> {
  return axis.map(value => ({
    key: value.key,
    label: value.key === NO_VALUE_KEY ? noValueLabel(fieldLabel) : value.label.text,
    totalCount: value.totalCount,
  }))
}

/** Maps one cell into the board's contract. Its cursor stays behind, because the board holds none. */
export function toBoardCell(cell: BoardPayloadCell): BoardData<BoardPayloadItem>['cells'][number] {
  return {
    columnKey: cell.columnKey,
    rowKey: cell.rowKey,
    items: cell.items,
    itemsLoaded: cell.itemsLoaded,
    totalCount: cell.totalCount,
    hasMoreItems: cell.hasMoreItems,
  }
}

/**
 * Maps the payload into the board's contract. Every stated fact carries across, except
 * `cellsTruncated`, which has no affordance to drive yet. Cursors and axis-field identifiers
 * do not either, because the board holds neither.
 */
export function toBoardData(payload: BoardInitialPayload): BoardData<BoardPayloadItem> {
  return {
    columns: toBoardAxisValues(payload.columns, payload.columnFieldLabel),
    rows: toBoardAxisValues(payload.rows, payload.rowFieldLabel),
    cells: payload.cells.map(toBoardCell),
    hasMoreColumns: payload.hasMoreColumns,
    hasMoreRows: payload.hasMoreRows,
    columnsTruncated: payload.columnsTruncated,
    rowsTruncated: payload.rowsTruncated,
    countsOverlap: payload.countsOverlap,
    pageSize: payload.pageSize,
  }
}
